import { SVGProps, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useNavigate, useParams } from 'react-router'

import { useNotifications } from 'app/application/application'
import { ConnectionIcon, SearchInput } from 'app/common/presentation'
import { CheckCircleIcon as CheckCircle, CircleIcon as Circle } from 'app/common/presentation/icons'
import { useConnections } from 'app/connections/application'
import { mapToConnectionName } from 'app/connections/presentation/connectionUrlMapper'
import { InstalledConnection } from 'app/connections/types'
import { useObjectives } from 'app/objectives/application'
import { Objective } from 'app/objectives/types'
import { useSignals, useTreatments } from 'app/signals/application'
import { CreateSignalContent, SignalPurpose, SignalType, Treatment } from 'app/signals/types'

import { Button, Checkbox, DropDown, Input, PageBanner, SegmentedControl } from 'components'

import styles from './CreateSignal.module.scss'

type AudiencePurpose = 'exclusion' | 'retargeting' | 'seed'
type ConversionPurpose = 'countMax' | 'valueMax'
type LearningScope = 'campaign' | 'treatment'

interface SignalConfigurationProps {
  platform: string
  variant: string
  signalType: string
}

interface AccountOption {
  id: string
  name: string
}

const signalTypes: SignalType[] = ['audience', 'conversion']
const signalPurposes: SignalPurpose[] = ['conversion', 'custom', 'valueBased']

const getAccounts = (connection: InstalledConnection): AccountOption[] => {
  switch (connection.name) {
    case 'facebook':
    case 'googleAdwords':
      return connection.config.accounts
    case 'criteo':
    case 'tiktok':
      return connection.config.advertisers
    default:
      return []
  }
}

const formatObjective = (objective: Objective) => {
  const aggregation =
    objective.aggregation.type === 'count' ? 'Count' : `Sum ${objective.aggregation.targetTrait}`

  return `${aggregation} / ${objective.direction} / per ${objective.per}`
}

const SignalConfiguration = ({ platform, variant, signalType }: SignalConfigurationProps) => {
  const { t } = useTranslation('signals')
  const { t: tConnections } = useTranslation('connections')
  const { connections } = useConnections()
  const { objectives } = useObjectives()
  const { getTreatments } = useTreatments()
  const { createSignal } = useSignals()
  const { createNotification, te } = useNotifications()
  const navigate = useNavigate()
  const [submitting, setSubmitting] = useState(false)

  const connectionName = mapToConnectionName(platform)
  const connection = connections.find(
    (item) => item.name === connectionName && item.status === 'active',
  )
  const validSignalType = signalTypes.includes(signalType as SignalType)
    ? (signalType as SignalType)
    : 'audience'
  const validPurpose = signalPurposes.includes(variant as SignalPurpose)
    ? (variant as SignalPurpose)
    : 'custom'
  const isAudience = validSignalType === 'audience'

  const [name, setName] = useState('')
  const [accountId, setAccountId] = useState<string | null>(null)
  const [audiencePurpose, setAudiencePurpose] = useState<AudiencePurpose>('seed')
  const [lookbackDays, setLookbackDays] = useState('')
  const [audienceSize, setAudienceSize] = useState('')
  const [conversionPurpose, setConversionPurpose] = useState<ConversionPurpose>('countMax')
  const [learningScope, setLearningScope] = useState<LearningScope>('campaign')
  const [objectiveId, setObjectiveId] = useState<string | null>(null)
  const [treatments, setTreatments] = useState<Treatment[]>([])
  const [selectedTreatments, setSelectedTreatments] = useState<string[]>([])
  const [treatmentSearch, setTreatmentSearch] = useState('')

  const accounts = useMemo(() => (connection ? getAccounts(connection) : []), [connection])
  const filteredObjectives = useMemo(
    () =>
      objectives.filter((objective) => {
        if (objective.status !== 'active') return false
        if (!isAudience && conversionPurpose === 'countMax') {
          return objective.aggregation.type === 'count'
        }
        if (validPurpose === 'valueBased' || (!isAudience && conversionPurpose === 'valueMax')) {
          return objective.aggregation.type === 'value'
        }
        return true
      }),
    [objectives, validPurpose, isAudience, conversionPurpose],
  )
  const visibleTreatments = useMemo(
    () =>
      treatments.filter((treatment) =>
        `${treatment.name} ${treatment.campaign.name}`
          .toLocaleLowerCase()
          .includes(treatmentSearch.toLocaleLowerCase()),
      ),
    [treatmentSearch, treatments],
  )

  useEffect(() => {
    if (!connection || !isAudience) return

    void getTreatments({ connection: [connection.id] }).then((result) => {
      if (result.ok) setTreatments(result.val)
    })
  }, [connection, getTreatments, isAudience])

  useEffect(() => {
    setObjectiveId((selected) =>
      filteredObjectives.some((objective) => objective.id === selected) ? selected : null,
    )
  }, [filteredObjectives])

  if (!connection) {
    return (
      <div className={styles.signalConfiguration}>
        <PageBanner subtitle={t('configuration.subtitle')} title={t('configuration.title')} />
        <p className={styles.configurationError}>{t('configuration.connectionUnavailable')}</p>
      </div>
    )
  }

  const ConnectionFieldIcon = (props: SVGProps<SVGSVGElement>) => (
    <ConnectionIcon {...props} connection={connection} />
  )
  const connectionLabel = tConnections(`available.${connection.name}.displayName`)
  const accountRequired =
    validSignalType === 'audience'
      ? ['facebook', 'criteo', 'tiktok'].includes(connection.name)
      : connection.name === 'googleAdwords'
  const creationSupported =
    validSignalType === 'audience'
      ? ['facebook', 'googleAnalytics', 'criteo', 'tiktok'].includes(connection.name) &&
        (validPurpose === 'custom' ||
          (validPurpose === 'valueBased' && connection.name === 'facebook'))
      : ['facebook', 'googleAdwords'].includes(connection.name) && validPurpose === 'conversion'
  const formValid =
    creationSupported &&
    name.trim().length > 0 &&
    objectiveId !== null &&
    (!accountRequired || accountId) &&
    (!isAudience ||
      ((lookbackDays === '' ||
        (Number.isFinite(Number(lookbackDays)) && Number(lookbackDays) > 0)) &&
        (audienceSize === '' ||
          (Number.isFinite(Number(audienceSize)) &&
            Number(audienceSize) > 0 &&
            Number(audienceSize) <= 100))))

  const handleCreate = async () => {
    if (!formValid || !objectiveId || submitting) return

    const base = { name: name.trim(), objective: objectiveId }

    let content: CreateSignalContent
    if (validSignalType === 'audience') {
      let destination: Extract<CreateSignalContent, { type: 'audience' }>['connection']
      if (connection.name === 'googleAnalytics') destination = { platform: connection.name }
      else if (
        (connection.name === 'facebook' ||
          connection.name === 'criteo' ||
          connection.name === 'tiktok') &&
        accountId
      ) {
        destination = { platform: connection.name, account: accountId }
      } else return
      content = {
        ...base,
        type: 'audience',
        connection: destination,
        config: {
          purpose: audiencePurpose,
          ...(lookbackDays !== '' ? { lookbackDays: Number(lookbackDays) } : {}),
          ...(audienceSize !== '' ? { size: Number(audienceSize) / 100 } : {}),
          ...(connection.name === 'facebook'
            ? { isValueBased: validPurpose === 'valueBased' }
            : {}),
          treatments: {
            scope: learningScope,
            ids: learningScope === 'campaign' ? [] : selectedTreatments,
          },
        },
      }
    } else {
      if (connection.name === 'googleAdwords' && accountId) {
        content = {
          ...base,
          type: 'conversion',
          connection: { platform: connection.name, account: accountId },
          config: { purpose: conversionPurpose },
        }
      } else if (connection.name === 'facebook') {
        content = {
          ...base,
          type: 'conversion',
          connection: { platform: connection.name },
          config: { purpose: conversionPurpose },
        }
      } else return
    }
    setSubmitting(true)
    try {
      const result = await createSignal(content)
      if (result.ok) await navigate('/signals')
      else
        createNotification({
          title: te(`http.${result.val.code}`),
          message: result.val.message,
          type: 'error',
        })
    } finally {
      setSubmitting(false)
    }
  }

  const toggleTreatment = (id: string) => {
    setSelectedTreatments((selection) =>
      selection.includes(id) ? selection.filter((selected) => selected !== id) : [...selection, id],
    )
  }

  return (
    <div className={styles.signalConfiguration}>
      <PageBanner
        subtitle={t(`configuration.${validSignalType}.subtitle`)}
        title={t(`configuration.${validSignalType}.title`)}
      />

      <div className={styles.configurationBody}>
        <section className={styles.configurationSection}>
          <div className={styles.sectionLabel}>
            <h2>{t('configuration.basics.title')}</h2>
            <p>{t(`configuration.${validSignalType}.basics`)}</p>
          </div>
          <div className={styles.configurationCard}>
            <div className={styles.configurationField}>
              <div className={styles.fieldLabel}>
                <label>{t('configuration.name.title')}</label>
              </div>
              <Input
                onChange={setName}
                placeholder={t('configuration.name.placeholder')}
                value={name}
              />
            </div>
            <div className={styles.configurationField}>
              <div className={styles.fieldLabel}>
                <label>{t('configuration.connection.title')}</label>
              </div>
              <Input iconLeft={ConnectionFieldIcon} value={connectionLabel} />
            </div>
            {accountRequired && (
              <div className={styles.configurationField}>
                <div className={styles.fieldLabel}>
                  <label>{t('configuration.adAccount.title')}</label>
                </div>
                <DropDown
                  data={accounts.map((account) => ({
                    label: `${account.name} (${account.id})`,
                    value: account.id,
                  }))}
                  onSelect={setAccountId}
                  placeholder={t('configuration.adAccount.placeholder')}
                  selected={accountId}
                />
              </div>
            )}
          </div>
        </section>

        <section className={styles.configurationSection}>
          <div className={styles.sectionLabel}>
            <h2>{t('configuration.settings.title')}</h2>
            <p>{t(`configuration.${validSignalType}.settings`)}</p>
          </div>
          <div className={styles.configurationCard}>
            {isAudience ? (
              <div className={styles.configurationField}>
                <div className={styles.fieldLabel}>
                  <label>{t('configuration.purpose.title')}</label>
                </div>
                <SegmentedControl
                  onChange={setAudiencePurpose}
                  options={(['seed', 'retargeting', 'exclusion'] as AudiencePurpose[]).map(
                    (value) => ({ label: t(`configuration.purpose.${value}`), value }),
                  )}
                  value={audiencePurpose}
                />
              </div>
            ) : (
              <div className={styles.configurationFieldTop}>
                <div className={styles.fieldLabel}>
                  <label>{t('configuration.purpose.title')}</label>
                </div>
                <div className={styles.purposeControl}>
                  <SegmentedControl
                    onChange={setConversionPurpose}
                    options={(['countMax', 'valueMax'] as ConversionPurpose[]).map((value) => ({
                      label: t(`configuration.purpose.${value}`),
                      value,
                    }))}
                    value={conversionPurpose}
                  />
                </div>
              </div>
            )}

            <div className={styles.configurationFieldTop}>
              <div className={styles.fieldLabel}>
                <label>{t('configuration.objective.title')}</label>
              </div>
              <div className={styles.objectiveList}>
                {filteredObjectives.map((objective) => (
                  <button
                    aria-pressed={objective.id === objectiveId}
                    className={styles.objectiveOption}
                    key={objective.id}
                    onClick={() => setObjectiveId(objective.id)}
                    type="button"
                  >
                    {objective.id === objectiveId ? <CheckCircle /> : <Circle />}
                    <span>
                      <strong>{objective.name}</strong>
                      <small>{formatObjective(objective)}</small>
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {isAudience && (
              <>
                <div className={styles.configurationField}>
                  <div className={styles.fieldLabel}>
                    <label>{t('configuration.lookbackDays.title')}</label>
                  </div>
                  <Input onChange={setLookbackDays} type="number" value={lookbackDays} />
                </div>
                <div className={styles.configurationField}>
                  <div className={styles.fieldLabel}>
                    <label>{t('configuration.audienceSize.title')}</label>
                  </div>
                  <Input
                    onChange={(value) => setAudienceSize(Number(value) > 100 ? '100' : value)}
                    type="number"
                    value={audienceSize}
                  />
                </div>
                <div className={styles.configurationField}>
                  <div className={styles.fieldLabel}>
                    <label>{t('configuration.learningScope.title')}</label>
                  </div>
                  <div className={styles.scopeOptions}>
                    {(['campaign', 'treatment'] as LearningScope[]).map((value) => (
                      <button
                        aria-pressed={value === learningScope}
                        className={styles.objectiveOption}
                        key={value}
                        onClick={() => setLearningScope(value)}
                        type="button"
                      >
                        {value === learningScope ? <CheckCircle /> : <Circle />}
                        <span>
                          <strong>{t(`configuration.learningScope.${value}`)}</strong>
                          <small>{t(`configuration.learningScope.${value}Description`)}</small>
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
                {learningScope === 'treatment' && (
                  <div className={styles.configurationFieldTop}>
                    <div className={styles.fieldLabel}>
                      <label>{t('configuration.treatments.title')}</label>
                    </div>
                    <div className={styles.treatmentsControl}>
                      <SearchInput
                        onChange={setTreatmentSearch}
                        placeholder={t('configuration.treatments.search')}
                        value={treatmentSearch}
                      />
                      <div className={styles.treatmentList}>
                        {visibleTreatments.map((treatment) => (
                          <button
                            aria-pressed={selectedTreatments.includes(treatment.id)}
                            className={styles.treatmentOption}
                            key={treatment.id}
                            onClick={() => toggleTreatment(treatment.id)}
                            type="button"
                          >
                            <Checkbox
                              checked={selectedTreatments.includes(treatment.id)}
                              decorative
                            />
                            <span>
                              <strong>{treatment.name}</strong>
                              <small>
                                {t(
                                  `configuration.treatments.status.${treatment.properties.status}`,
                                )}
                              </small>
                            </span>
                          </button>
                        ))}
                      </div>
                      <span className={styles.selectedCount}>
                        {t('configuration.treatments.selected', {
                          count: selectedTreatments.length,
                        })}
                      </span>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </div>

      <footer className={styles.configurationFooter}>
        <Button disabled={!formValid} loading={submitting} onClick={() => void handleCreate()}>
          {t('configuration.action.create')}
        </Button>
      </footer>
    </div>
  )
}

const CreateSignal = () => {
  const { platform, signalType, variant } = useParams()

  if (
    !platform ||
    !signalType ||
    !signalTypes.includes(signalType as SignalType) ||
    (variant !== undefined &&
      !(signalType === 'audience'
        ? ['custom', 'valueBased'].includes(variant)
        : variant === 'conversion'))
  ) {
    return <Navigate replace to="/signals/new" />
  }

  return (
    <SignalConfiguration
      key={`${platform}/${signalType}/${variant ?? ''}`}
      platform={platform}
      signalType={signalType}
      variant={variant ?? (signalType === 'conversion' ? 'conversion' : 'custom')}
    />
  )
}

export default CreateSignal
